import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { SuggestionSearchBarOptionBase } from '@aibees/elephant/feature/suggestion-search-bar';
import {
  SEARCH_PARAM_VALUE_KEY,
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { useAutoSuggestedPeople } from '@aibees/elephant/hooks/people/use-auto-suggested-people';
import { SearchType } from '@aibees/elephant/types';
import { useLookalikeSeedsStore } from '@aibees/elephant/store/lookalike-seeds';
import { sanitizeLinkedinUrl } from '@aibees/elephant/utils/url';
import { classnames } from '@aibees/shared/utils';
import { Button } from '@mui/material';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLookalikeSeeds } from '../company-similarity-search/hooks/use-lookalike-seeds';
import * as Styled from './styles';
import * as SX from './sx';
import type { PeopleSimilaritySearchProps as Props } from './types';

// TODO @Mohammad79-nkv please check the error eslint-disable-next-line react-hooks/rules-of-hooks

export const PeopleSimilaritySearch = memo(
  ({ className, compact, renderMode, dialogState }: Props) => {
    const { setParams, params, getParam, removeParam } = useSearchQueryFilter();
    const { track } = useTrackEvents();
    const navigate = useNavigate();

    const type = useSearchTypeFromRoute();
    const searchKey = SEARCH_PARAM_VALUE_KEY;
    const defaultValues = useLookalikeSeeds();
    const mergeSeeds = useLookalikeSeedsStore((state) => state.mergeSeeds);

    const handleSearch = (values: SuggestionSearchBarOptionBase[]) => {
      const searchValues: string[] = values.map((i) => i.value);

      // Cache the picked chips' metadata so the results page renders them
      // instantly — no browse round-trip and no loading flash on the chips.
      mergeSeeds(String(SearchType.People), values);

      if (type !== SearchType.People) {
        const queryString = new URLSearchParams({
          [searchKey]: searchValues.join(FILTER_VALUE_SEPARATOR),
        }).toString();
        navigate(`/search/people?${queryString}`, { replace: true });
        dialogState?.onFalse();
        return;
      }

      setParams({
        ...params,
        ...{ [searchKey]: searchValues.join(FILTER_VALUE_SEPARATOR) },
      });

      if (type)
        track(type, {
          input: searchValues,
        });

      dialogState?.onFalse();
    };

    const handleDelete = (
      linkedinUrl: string,
      values: SuggestionSearchBarOptionBase[]
    ) => {
      const value = getParam('value');
      if (value) {
        const draft = values.filter((i) => i.value !== linkedinUrl);
        handleSearch(draft);
        if (!draft.length) {
          removeParam(String(searchKey));
        }
      }
    };

    const dialogMode = renderMode === 'dialog';
    const pageMode = renderMode === 'page';

    const buttonText = compact ? 'Search' : '⚡ Find Similarity';

    return (
      <Styled.Wrapper
        className={classnames('PeopleSimilaritySearch', className)}
        sx={SX.root}
      >
        <Styled.SearchBar
          renderMode={renderMode}
          searchCategory="people"
          searchIcon="linkedin-contain"
          className={classnames({ compact })}
          multiple
          limit={5}
          limitErrorMsg="Max 5 lookalikes has been reached."
          placeholder="Enter Name or Paste the LinkedIn URL here to find similar profiles"
          searchButton={
            <Button
              variant="contained"
              color="primary"
              size="medium"
              className={classnames({ dialogMode })}
            >
              {dialogMode && 'Find Similarity'}
              {pageMode && buttonText}
            </Button>
          }
          onClickSearchButton={handleSearch}
          fetchOptions={(searchValue: string) =>
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useAutoSuggestedPeople(sanitizeLinkedinUrl(searchValue))
          }
          defaultValue={defaultValues}
          onDelete={(i, searchValue) => handleDelete(i.value!, searchValue)}
        />
      </Styled.Wrapper>
    );
  }
);

PeopleSimilaritySearch.displayName = 'PeopleSimilaritySearch';
